import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Modal, 
  Spin, 
  Typography, 
  Space, 
  Button, 
  Input, 
  Tooltip,
  message,
  Divider,
  Tag,
  Empty,
  Alert
} from 'antd';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const { Text, Title, Paragraph } = Typography;
const { Search } = Input;

const DocumentPreviewModal = ({ 
  visible, 
  document: docProp, 
  onClose 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  
  const containerRef = useRef(null);
  const modalRef = useRef(null);

  const fileType = docProp?.file_type?.toLowerCase() || '';
  const isPdf = fileType === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'tiff', 'tif'].includes(fileType);
  const isText = ['txt', 'md', 'csv'].includes(fileType);
  const isMarkdown = fileType === 'md';
  const isOffice = ['docx', 'doc', 'xlsx', 'pptx'].includes(fileType);

  useEffect(() => {
    if (!visible || !docProp) {
      setLoading(false);
      setFileUrl(null);
      setTextContent(null);
      setNumPages(null);
      setCurrentPage(1);
      setScale(1.0);
      setSearchText('');
      setSearchResults([]);
      setError(null);
      return;
    }

    const loadDocument = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (isPdf || isImage) {
          const token = localStorage.getItem('token');
          const baseUrl = api.defaults.baseURL || '';
          const url = `${baseUrl}/documents/${docProp.id}/file`;
          
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            throw new Error('Failed to load document');
          }
          
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setFileUrl(objectUrl);
        }
        
        if (isText || isOffice) {
          const response = await api.get(`/documents/${docProp.id}/content`);
          setTextContent(response.data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError(err.message || 'Failed to load document');
        setLoading(false);
      }
    };

    loadDocument();

    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [visible, docProp, isPdf, isImage, isText, isOffice]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.25));
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, numPages || 1));
  };

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      if (modalRef.current?.requestFullscreen) {
        modalRef.current.requestFullscreen();
      }
    } else {
      if (window.document.exitFullscreen) {
        window.document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!window.document.fullscreenElement);
    };
    
    window.document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleSearch = (value) => {
    if (!value || !textContent?.content) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }
    
    const text = textContent.content.toLowerCase();
    const searchLower = value.toLowerCase();
    const results = [];
    let index = 0;
    
    while ((index = text.indexOf(searchLower, index)) !== -1) {
      results.push(index);
      index += searchLower.length;
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(0);
    
    if (results.length > 0) {
      message.success(`Found ${results.length} matches`);
    } else {
      message.info('No matches found');
    }
  };

  const handleDownload = async () => {
    if (!docProp) return;
    
    try {
      const token = localStorage.getItem('token');
      const baseUrl = api.defaults.baseURL || '';
      const url = `${baseUrl}/documents/${docProp.id}/file`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = downloadUrl;
      a.download = docProp.original_filename || 'document';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      
      message.success('Document downloaded successfully');
    } catch (err) {
      message.error('Failed to download document');
    }
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? 
        <mark key={i} style={{ backgroundColor: '#ffc107', padding: '0 2px' }}>{part}</mark> : 
        part
    );
  };

  const renderPdfViewer = () => (
    <div 
      ref={containerRef}
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        overflow: 'auto',
        maxHeight: isFullscreen ? '100vh' : 'calc(80vh - 120px)',
        padding: '16px',
        backgroundColor: '#525659'
      }}
    >
      <Document
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={<Spin size="large" />}
        error={<Alert type="error" message="Failed to load PDF" />}
      >
        <Page 
          pageNumber={currentPage} 
          scale={scale}
          loading={<Spin />}
          renderTextLayer={true}
          renderAnnotationLayer={true}
        />
      </Document>
    </div>
  );

  const renderImageViewer = () => (
    <div 
      style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        overflow: 'auto',
        maxHeight: isFullscreen ? '100vh' : 'calc(80vh - 120px)',
        padding: '16px',
        backgroundColor: '#f5f5f5'
      }}
    >
      <img 
        src={fileUrl} 
        alt={docProp?.original_filename}
        style={{ 
          maxWidth: '100%',
          maxHeight: '100%',
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          transition: 'transform 0.2s ease'
        }}
      />
    </div>
  );

  const renderTextViewer = () => {
    if (!textContent) return null;
    
    const content = textContent.content || '';
    const displayContent = searchText ? 
      highlightSearchText(content, searchText) : 
      content;

    return (
      <div 
        style={{ 
          overflow: 'auto',
          maxHeight: isFullscreen ? '100vh' : 'calc(80vh - 120px)',
          padding: '24px',
          backgroundColor: '#fff',
          fontSize: `${14 * scale}px`,
          lineHeight: 1.6
        }}
      >
        {isMarkdown ? (
          <div className="markdown-preview">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          </div>
        ) : (
          <pre style={{ 
            whiteSpace: 'pre-wrap', 
            wordBreak: 'break-word',
            fontFamily: fileType === 'csv' ? 'monospace' : 'inherit',
            margin: 0
          }}>
            {typeof displayContent === 'string' ? displayContent : displayContent}
          </pre>
        )}
      </div>
    );
  };

  const renderOfficeViewer = () => {
    if (!textContent) return null;
    
    return (
      <div 
        style={{ 
          overflow: 'auto',
          maxHeight: isFullscreen ? '100vh' : 'calc(80vh - 120px)',
          padding: '24px',
          backgroundColor: '#fff'
        }}
      >
        <Alert 
          type="info" 
          message="Document Preview" 
          description="This is the extracted text content from the document. For full formatting, please download the original file."
          style={{ marginBottom: '16px' }}
          showIcon
        />
        <div style={{ 
          fontSize: `${14 * scale}px`,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {searchText ? 
            highlightSearchText(textContent.content || '', searchText) : 
            (textContent.content || 'No content available')}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <Spin size="large" tip="Loading document..." />
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical">
                <Text type="danger">{error}</Text>
                <Button onClick={() => setLoading(true)}>Retry</Button>
              </Space>
            }
          />
        </div>
      );
    }

    if (isPdf) return renderPdfViewer();
    if (isImage) return renderImageViewer();
    if (isText) return renderTextViewer();
    if (isOffice) return renderOfficeViewer();

    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <Empty
          description={
            <Space direction="vertical">
              <Text>Preview not available for this file type</Text>
              <Button icon={<DownloadOutlined />} onClick={handleDownload}>
                Download to view
              </Button>
            </Space>
          }
        />
      </div>
    );
  };

  const getFileTypeIcon = () => {
    if (isPdf) return <FilePdfOutlined style={{ color: '#DC2626' }} />;
    if (isImage) return <FileImageOutlined style={{ color: '#0891B2' }} />;
    return <FileTextOutlined style={{ color: '#6B7280' }} />;
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={isFullscreen ? '100vw' : '90vw'}
      style={{ 
        top: isFullscreen ? 0 : 20,
        maxWidth: isFullscreen ? '100vw' : '1400px',
        padding: 0
      }}
      styles={{
        body: { 
          padding: 0,
          maxHeight: isFullscreen ? '100vh' : '85vh',
          overflow: 'hidden'
        }
      }}
      footer={null}
      closable={false}
      destroyOnClose
    >
      <div ref={modalRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa'
        }}>
          <Space>
            {getFileTypeIcon()}
            <Title level={5} style={{ margin: 0 }}>
              {docProp?.original_filename || 'Document'}
            </Title>
            <Tag color="blue">{fileType?.toUpperCase() || 'FILE'}</Tag>
          </Space>
          
          <Space split={<Divider type="vertical" />}>
            {(isText || isOffice) && (
              <Search
                placeholder="Search in document"
                onSearch={handleSearch}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 200 }}
                size="small"
                allowClear
              />
            )}
            
            <Space>
              <Tooltip title="Zoom Out">
                <Button 
                  icon={<ZoomOutOutlined />} 
                  onClick={handleZoomOut}
                  disabled={scale <= 0.25}
                  size="small"
                />
              </Tooltip>
              <Text style={{ minWidth: '50px', textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </Text>
              <Tooltip title="Zoom In">
                <Button 
                  icon={<ZoomInOutlined />} 
                  onClick={handleZoomIn}
                  disabled={scale >= 3.0}
                  size="small"
                />
              </Tooltip>
            </Space>
            
            {isPdf && numPages && (
              <Space>
                <Tooltip title="Previous Page">
                  <Button 
                    icon={<LeftOutlined />} 
                    onClick={handlePrevPage}
                    disabled={currentPage <= 1}
                    size="small"
                  />
                </Tooltip>
                <Text>
                  {currentPage} / {numPages}
                </Text>
                <Tooltip title="Next Page">
                  <Button 
                    icon={<RightOutlined />} 
                    onClick={handleNextPage}
                    disabled={currentPage >= numPages}
                    size="small"
                  />
                </Tooltip>
              </Space>
            )}
            
            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <Button 
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} 
                onClick={toggleFullscreen}
                size="small"
              />
            </Tooltip>
            
            <Tooltip title="Download">
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleDownload}
                size="small"
              />
            </Tooltip>
            
            <Tooltip title="Close">
              <Button 
                icon={<CloseOutlined />} 
                onClick={onClose}
                size="small"
              />
            </Tooltip>
          </Space>
        </div>
        
        {searchResults.length > 0 && (
          <div style={{ 
            padding: '8px 16px', 
            backgroundColor: '#e6f7ff',
            borderBottom: '1px solid #91d5ff'
          }}>
            <Text>
              {searchResults.length} matches found
            </Text>
          </div>
        )}
        
        {renderContent()}
      </div>
    </Modal>
  );
};

export default DocumentPreviewModal;
